import { forceSimulation, forceLink, forceManyBody, forceX, forceY, randomLcg } from 'd3'
import geometry from './contour-geometry.cjs'
import { defaultNodes } from './document.mjs'
import { nodeLabels, nodeWidth } from './node-display.mjs'
import { layoutUnits } from './unit-layout.mjs'

const overlap = (a, b, gap = 0) =>
  Math.abs(a.x - b.x) < (a.width + b.width) / 2 + gap && Math.abs(a.y - b.y) < (a.height + b.height) / 2 + gap

function separateRectangles(nodes) {
  for (let pass = 0; pass < 120; pass++) {
    let changed = false
    for (let i = 0; i < nodes.length; i++)
      for (let j = i + 1; j < nodes.length; j++) {
        const a = nodes[i],
          b = nodes[j]
        if (!overlap(a, b, 20)) continue
        const dx = (a.width + b.width) / 2 + 20 - Math.abs(a.x - b.x)
        const dy = (a.height + b.height) / 2 + 20 - Math.abs(a.y - b.y)
        const axis = dx < dy ? 'x' : 'y',
          shift = ((axis === 'x' ? dx : dy) + 0.1) / 2
        const direction = a[axis] <= b[axis] ? -1 : 1
        a[axis] += shift * direction
        b[axis] -= shift * direction
        changed = true
      }
    if (!changed) return
  }
  // A deterministic shelf layout is a terminating fallback for dense rectangles.
  let x = 0,
    y = 0,
    rowHeight = 0
  nodes.forEach((n, i) => {
    if (i % 3 === 0) {
      x = 0
      y += rowHeight + 24
      rowHeight = 0
    }
    n.x = x + n.width / 2
    n.y = y + n.height / 2
    x += n.width + 24
    rowHeight = Math.max(rowHeight, n.height)
  })
}

export function layoutDocument(document, expanded = new Set(), positions = {}, schemas = defaultNodes) {
  const paragraphs = document.paragraphs.filter((p) => p.kind === 'paragraph')
  if (!paragraphs.length)
    return { nodes: [], units: [], bubbles: [], bounds: { x: 0, y: 0, width: 200, height: 200 } }
  const unitMap = new Map(
    document.paragraphs.map((p) => [p.id, document.units.filter((u) => u.paragraphId === p.id)]),
  )
  const labels = nodeLabels(document, schemas)
  const nodes = paragraphs.map((p, index) => {
    const units = unitMap.get(p.id),
      open = expanded.has(p.id)
    const label = labels.get(p.id)
    const labelWidth = nodeWidth(label)
    const interior = open ? layoutUnits(units, document.edges, labels, labelWidth) : null
    return {
      ...p,
      label,
      open,
      units,
      interior,
      x: Math.cos(index * 2.4) * 130,
      y: Math.sin(index * 2.4) * 130,
      width: open ? interior.width : Math.max(83, labelWidth),
      height: open ? interior.height : 35,
    }
  })
  const groups = [null, ...document.chapters.map((c) => c.id)]
    .map((id, index) => {
      const members = nodes.filter((n) => n.chapterId === id)
      if (!members.length) return null
      const ids = new Set(members.map((n) => n.id))
      const links = document.edges
        .filter((e) => e.level === 'paragraph' && ids.has(e.source) && ids.has(e.target))
        .map((e) => ({ ...e }))
      const simulation = forceSimulation(members)
        .randomSource(randomLcg(index + 42))
        .stop()
        .force(
          'link',
          forceLink(links)
            .id((n) => n.id)
            .distance((e) => (e.source.width + e.target.width) / 2 + 50)
            .strength(0.25),
        )
        .force('charge', forceManyBody().strength(-420))
        .force('x', forceX(0).strength(0.045))
        .force('y', forceY(0).strength(0.045))
      simulation.tick(160)
      simulation.stop()
      members.forEach((n) => {
        if (positions[n.id]) Object.assign(n, positions[n.id])
      })
      separateRectangles(members)
      const shape = geometry.build(members, {
        outerPadding: 22,
        outerGap: 28,
        contourDetail: 140,
        contourSmooth: 20,
      })
      members.forEach((n) => {
        n.localX = n.x
        n.localY = n.y
        n.x -= shape.x
        n.y -= shape.y
      })
      const angle = (index - 1) * 2.35 - 0.7
      return {
        id,
        members,
        shape,
        x: index ? Math.cos(angle) * 260 : -360,
        y: index ? Math.sin(angle) * 260 : -360,
        width: shape.width,
        height: shape.height,
      }
    })
    .filter(Boolean)
  // Test decomposed concave polygons; bounding boxes only skip distant pairs.
  for (let pass = 0; pass < 140; pass++) {
    let changed = false
    for (let i = 0; i < groups.length; i++)
      for (let j = i + 1; j < groups.length; j++) {
        const a = groups[i],
          b = groups[j]
        if (!overlap(a, b, 32)) continue
        const hit = geometry.contact(a.shape.parts, a, b.shape.parts, b)
        if (!hit) continue
        const scale = (hit.depth + 0.1) / hit.depth / 2
        a.x -= hit.x * scale
        a.y -= hit.y * scale
        b.x += hit.x * scale
        b.y += hit.y * scale
        changed = true
      }
    if (!changed) break
  }
  // Rare contact cycles are resolved by moving only the colliding group outward.
  groups.forEach((b, i) => {
    let attempts = 0
    while (
      groups
        .slice(0, i)
        .some((a) => overlap(a, b, 32) && geometry.contact(a.shape.parts, a, b.shape.parts, b))
    ) {
      b.x += 24
      if (++attempts > 300) throw new Error('章节轮廓布局未能收敛。')
    }
  })
  const bubbles = groups
    .filter((g) => g.id)
    .map((g) => ({
      id: g.id,
      ...document.chapters.find((c) => c.id === g.id),
      polygon: geometry.translate(g.shape.polygon, g.x, g.y),
      titleX: g.x + g.shape.title.x,
      titleY: g.y + g.shape.title.y,
      x: g.x,
      y: g.y,
      width: g.width,
      height: g.height,
    }))
  groups.forEach((g) =>
    g.members.forEach((n) => {
      n.x += g.x
      n.y += g.y
    }),
  )
  const visibleUnits = nodes
    .filter((n) => n.open)
    .flatMap((n) =>
      n.interior.nodes.map((u) => ({
        ...u,
        x: n.x + u.x,
        y: n.y + u.y,
      })),
    )
  const rects = [...nodes, ...bubbles]
  const minX = Math.min(...rects.map((n) => n.x - n.width / 2)) - 30,
    minY = Math.min(...rects.map((n) => n.y - n.height / 2)) - 30
  const maxX = Math.max(...rects.map((n) => n.x + n.width / 2)) + 30,
    maxY = Math.max(...rects.map((n) => n.y + n.height / 2)) + 30
  return {
    nodes,
    units: visibleUnits,
    bubbles,
    bounds: { x: minX, y: minY, width: Math.max(200, maxX - minX), height: Math.max(200, maxY - minY) },
  }
}

export function edgeGeometry(a, b) {
  const dx = b.x - a.x,
    dy = b.y - a.y
  const boundary = (n, x, y) => {
    const scale = Math.min(n.width / 2 / (Math.abs(x) || 0.001), n.height / 2 / (Math.abs(y) || 0.001))
    return { x: n.x + x * scale, y: n.y + y * scale }
  }
  const start = boundary(a, dx, dy),
    end = boundary(b, -dx, -dy)
  const offset = Math.min(35, Math.hypot(dx, dy) * 0.12)
  const length = Math.hypot(dx, dy) || 1
  const normal = { x: -dy / length, y: dx / length }
  const control = {
    x: (start.x + end.x) / 2 + normal.x * offset,
    y: (start.y + end.y) / 2 + normal.y * offset,
  }
  return {
    path: `M${start.x},${start.y} Q${control.x},${control.y} ${end.x},${end.y}`,
    midpoint: { x: (start.x + 2 * control.x + end.x) / 4, y: (start.y + 2 * control.y + end.y) / 4 },
    normal,
  }
}
export const edgePath = (a, b) => edgeGeometry(a, b).path
